import type { BaseHttpRequest } from '@/api/generated-client/core/BaseHttpRequest';
import { BaseApi } from '@/api/base-api';
import {
  CreateGamesOfWeekRequest,
  GameOfWeekDeleteItemRequest,
  GameOfWeekListResponse,
  GameOfWeekResponse
} from "@/open-api/models";

export interface GetGamesOfWeekListQuery {
  presetId: string;
  pageNumber?: number;
  pageSize?: number;
  gameId?: string;
  localeIds?: number[];
}

export class GamesOfWeekApi extends BaseApi {
  private controller = '/common/catalog';

  constructor(public readonly httpRequest: BaseHttpRequest) {
    super();
  }

  private withQuery<T extends object>(path: string, query?: T): string {
    const queryString = this.queryToQueryString(query);
    return queryString.length > 0 ? `${path}?${queryString}` : path;
  }

  private buildListQueryString(query: GetGamesOfWeekListQuery): string {
    const searchParams = new URLSearchParams();
    const trimmedPresetId = query.presetId.trim();

    searchParams.set('presetId', trimmedPresetId);
    searchParams.set('page.number', String(query.pageNumber ?? 1));
    searchParams.set('page.size', String(query.pageSize ?? 15));

    const trimmedGameId = query.gameId?.trim();
    if (trimmedGameId) {
      searchParams.set('gameId', trimmedGameId);
    }

    for (const localeId of query.localeIds ?? []) {
      searchParams.append('localeIds', String(localeId));
    }

    return searchParams.toString();
  }

  private isNotFoundError(error: unknown): boolean {
    const maybeError = error as {
      status?: number;
      code?: number;
      title?: string;
    };

    if (maybeError.status === 404 || maybeError.code === 104) {
      return true;
    }

    return maybeError?.title === "NOT_FOUND_CODE";
  }

  public async getBaseGameOfWeek(
    query: { presetId: string },
  ): Promise<GameOfWeekResponse | null> {
    try {
      return await this.httpRequest.rpcRequest(
        this.controller,
        `[GET] ${this.withQuery('/api/v1/games-of-week/base', query)}`,
        {},
      );
    } catch (error) {
      if (this.isNotFoundError(error)) {
        return null;
      }

      throw error;
    }
  }

  public async listGamesOfWeek(
    query: GetGamesOfWeekListQuery,
  ): Promise<GameOfWeekListResponse> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/games-of-week?${this.buildListQueryString(query)}`,
      {},
    );
  }

  public async putGameOfWeek(
    payload: CreateGamesOfWeekRequest,
    options?: { force?: boolean },
  ): Promise<GameOfWeekResponse> {
    const queryString = options?.force === true
      ? this.queryToQueryString({ force: true })
      : '';
    const url = queryString.length > 0
      ? `/api/v1/games-of-week?${queryString}`
      : '/api/v1/games-of-week';

    return this.httpRequest.rpcRequest(
      this.controller,
      `[PUT] ${url}`,
      payload,
    );
  }

  public async deleteGamesOfWeekBatch(
    payload: GameOfWeekDeleteItemRequest[],
  ): Promise<void> {
    return this.httpRequest.rpcRequest(
      this.controller,
      '[DELETE] /api/v1/games-of-week/batch',
      payload,
    );
  }
}
